import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { languageNames } from '../i18n/translations';
import { api } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Gear, Globe, Key } from '@phosphor-icons/react';

export const SettingsPage = () => {
  const { user, updateLanguage } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [loading, setLoading] = useState(false);

  const handleLanguageChange = async (lang) => {
    try {
      await updateLanguage(lang);
      changeLanguage(lang);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      toast.error('Le password non coincidono');
      return;
    }
    
    if (passwords.new.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      await api.updatePassword({
        current_password: passwords.current,
        new_password: passwords.new
      });
      toast.success(t('settings.passwordChanged'));
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="settings-title">
          {t('settings.title')}
        </h1>
      </div>

      <div className="content-area">
        <div className="max-w-2xl space-y-6">
          {/* User Info */}
          <div className="bg-white border border-[#E4E4E7] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                <Gear size={20} className="text-[#52525B]" />
              </div>
              <h2 className="text-lg font-semibold text-[#09090B]">Account</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">Nome</p>
                <p className="text-sm text-[#09090B]">{user?.first_name} {user?.last_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">Email</p>
                <p className="text-sm text-[#09090B]">{user?.email}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">Cliente</p>
                <p className="text-sm text-[#09090B]">{user?.client_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B] mb-1">Ruolo</p>
                <p className="text-sm text-[#09090B]">{t(`admin.roles.${user?.role}`)}</p>
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="bg-white border border-[#E4E4E7] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                <Globe size={20} className="text-[#52525B]" />
              </div>
              <h2 className="text-lg font-semibold text-[#09090B]">{t('settings.language')}</h2>
            </div>
            
            <div className="flex gap-2">
              {Object.entries(languageNames).map(([code, name]) => (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  className={`px-4 py-2 text-sm font-medium border transition-all duration-200 ${
                    language === code
                      ? 'bg-[#09090B] text-white border-[#09090B]'
                      : 'bg-white text-[#09090B] border-[#E4E4E7] hover:border-[#09090B]'
                  }`}
                  data-testid={`settings-lang-${code}`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-white border border-[#E4E4E7] p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                <Key size={20} className="text-[#52525B]" />
              </div>
              <h2 className="text-lg font-semibold text-[#09090B]">{t('settings.changePassword')}</h2>
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('settings.currentPassword')}
                </Label>
                <Input
                  type="password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  className="input-swiss mt-1"
                  required
                  data-testid="current-password"
                />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('settings.newPassword')}
                </Label>
                <Input
                  type="password"
                  value={passwords.new}
                  onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  className="input-swiss mt-1"
                  required
                  data-testid="new-password"
                />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.2em] text-[#52525B]">
                  {t('settings.confirmPassword')}
                </Label>
                <Input
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  className="input-swiss mt-1"
                  required
                  data-testid="confirm-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#002FA7] hover:bg-[#002075] text-white rounded-sm"
                data-testid="save-password"
              >
                {loading ? t('common.loading') : t('settings.save')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
